import Pill, { PillProps } from 'ext-ui/components/Pill/Pill'
import React, { ReactElement } from 'react'

import { PhysicalCardManagementStatus } from '../cardDetailHeroTypes'
import SvgLock from '../icons/SvgLock'
import SvgSnowflake from '../icons/SvgSnowflake'
import { useCardStatusLabel } from '../useCardStatusLabel'
import * as style from './StatusPill.style'

/**
 * Colour never carries the status alone — every state also has a distinct
 * label, per the WCAG rule that colour must not be the only means of conveying
 * information.
 *
 * FROZEN, REPLACEMENT_PENDING, and LOCKED are intentionally different: FROZEN
 * is reversible, REPLACEMENT_PENDING is an ordinary-user terminal lifecycle
 * state, and LOCKED is issuer-imposed and clears through the unlock-request
 * capability.
 */
export const statusPillColor = (
  status: PhysicalCardManagementStatus,
): PillProps['color'] => {
  switch (status) {
    case 'ACTIVE':
      return 'highlight'
    case 'PENDING_ISSUANCE':
      return 'pending'
    case 'FROZEN':
      // Blue ("cold") to match the freeze language in the design — distinct from
      // the amber pending-issuance state and the severe (red) locked state.
      return 'saas'
    case 'REPLACEMENT_PENDING':
      return 'saas'
    case 'LOCKED':
    case 'FAILED':
      return 'severe'
    case 'CANCELED':
      return undefined
  }
}

/**
 * Optional leading glyph in the status pill, where the design uses one: frozen
 * carries a snowflake (the "cold" freeze language) and locked a padlock. Other
 * states are label-only. The glyph inherits the pill's text colour via
 * currentColor.
 */
export const statusPillIcon = (
  status: PhysicalCardManagementStatus,
): ReactElement | null => {
  switch (status) {
    case 'FROZEN':
      return <SvgSnowflake width={11} height={11} />
    case 'REPLACEMENT_PENDING':
    case 'LOCKED':
      return <SvgLock width={11} height={11} />
    default:
      return null
  }
}

type StatusPillProps = {
  status: PhysicalCardManagementStatus
}

/**
 * The card status pill (colour + optional glyph + label), lifted out of
 * `CardSummary` so the full hero and the pinned compact bar render exactly the
 * same pill from one source — no per-variant fork of the colour/glyph mapping.
 * The label is always present so colour is never the only signal.
 */
const StatusPill: React.FC<StatusPillProps> = ({ status }): ReactElement => {
  const statusLabel = useCardStatusLabel()

  return (
    <Pill color={statusPillColor(status)} size="small">
      <span css={style.pillContent} data-testid="status-pill-content">
        {statusPillIcon(status)}
        {statusLabel(status)}
      </span>
    </Pill>
  )
}

export default StatusPill
